import { CURRENCY_TO_REGION_ID } from '@src/config/localisation/locale-config.constants'
import { LOCALE_COOKIE, REGION_ID_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import { REGION_ID_HEADER, REGION_LANG_HEADER } from '@src/constants/api.headers.constants'
import { getSafeLocationHref } from '@src/telemetry/integrations/provider-utils/safe-location-utils'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { generateUniqueEventId, trackInternalEvent } from '@src/telemetry/track'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import getCookie from '../../utils/getCookie'
import { getPageContextDetails } from '../../utils/getPageContextDetails'
import isBrowser from '../../utils/isBrowser'

import type { Cart } from './cart.service.types'

/**
 * Create-time counterpart to report-cart-region-mismatch.ts. The 422 diagnostic only sees the
 * corrected region on the RETURN visit; it cannot show which region a guest cart was BORN with.
 * This fires once when a guest cart is created and records the region actually SENT on the create
 * request alongside the region the created cart came back as, plus every cookie/localStorage/SSR
 * source — so a mis-tag at birth (e.g. header dropped → backend FALLBACK_REGION_ID) is attributable
 * without waiting for the 422 or reproducing the entry path.
 * See GDoc "Region ID mismatch Error | Action Plan".
 */
interface NextDataLike {
  props?: { pageProps?: { _nextI18Next?: { initialLocale?: string } } }
}

/** The shape we consume off the cart-create call: the Axios response for `POST /carts`. */
interface CartCreateResponseLike {
  data?: unknown
  config?: { headers?: Record<string, unknown> }
}

/** The locale the Next.js server resolved for this request, held in memory — not the cookie. */
const getServerLocaleInMemory = (): string | null => {
  if (!isBrowser()) return null
  try {
    const nextData = (window as unknown as { __NEXT_DATA__?: NextDataLike }).__NEXT_DATA__
    return nextData?.props?.pageProps?._nextI18Next?.initialLocale ?? null
  } catch {
    return null
  }
}

/**
 * Reads a header off the create request's own config, i.e. the value the BaseHttpClient localisation
 * interceptor actually sent. Deliberately NOT recomputed via `getLocalisationHeaders()`: the
 * interceptor calls it with the request's `baseURL`, so a recomputation here (no domain, and a moment
 * later) can report a header the request never carried — which would misattribute the very cause this
 * diagnostic exists to find. Mirrors `readSentHeader` in report-cart-region-mismatch.ts.
 */
const readSentHeader = (res: CartCreateResponseLike | null, headerKey: string): string | null => {
  const headers = res?.config?.headers ?? {}
  const value = headers[headerKey] ?? headers[headerKey.toLowerCase()]
  return value == null ? null : String(value)
}

/**
 * Unwraps the created cart from every create-response shape:
 *   carts              -> the cart itself
 *   checkouts          -> { ..., cart }
 *   previous-cart      -> { createdCart, cart }
 *   previous-checkout  -> { createdCheckout, checkout: { ..., cart } }
 */
const pickCart = (data: unknown): Partial<Cart> | null => {
  if (!data || typeof data !== 'object') return null
  let record = data as Record<string, unknown>
  if (record.checkout && typeof record.checkout === 'object') {
    record = record.checkout as Record<string, unknown>
  }
  const nested = record.cart
  if (nested && typeof nested === 'object') return nested as Partial<Cart>
  return record as Partial<Cart>
}

/** cart.regionId when present, else derived from the (required) currencyCode, else null. */
const resolveCartRegionId = (cart: Partial<Cart> | null): number | null => {
  if (!cart) return null
  if (typeof cart.regionId === 'number' && Number.isFinite(cart.regionId)) return cart.regionId
  if (cart.currencyCode) return CURRENCY_TO_REGION_ID[cart.currencyCode] ?? null
  return null
}

/**
 * The client-side region identifiers as they stood at one instant. Capture this immediately BEFORE
 * the create request so the reported cookie / localStorage / B2B values are the ones in effect when
 * the cart was actually born — not whatever they became during the network round-trip. `REGION_DETAILS`
 * localStorage in particular is written by the locale provider's mount effect, so reading it after the
 * response can report a region that did not exist when the cart was created, masking the page-vs-cart
 * split this diagnostic exists to expose.
 */
export interface CartRegionSnapshot {
  region_id_cookie: string | null
  locale_cookie: string | null
  region_id_local_storage: number | null
  locale_local_storage: string | null
  server_locale_in_memory: string | null
  is_b2b: boolean
  referrer: string | null
  page_path: string | null
  page_url: string
}

export const captureCartRegionSnapshot = (): CartRegionSnapshot | null => {
  if (!isBrowser()) return null
  try {
    const lsData = getLocalisationDataFromStorage()
    return {
      region_id_cookie: getCookie(REGION_ID_COOKIE) ?? null,
      locale_cookie: getCookie(LOCALE_COOKIE) ?? null,
      region_id_local_storage: lsData?.regionId ?? null,
      locale_local_storage: lsData?.locale ?? null,
      server_locale_in_memory: getServerLocaleInMemory(),
      is_b2b: !!getStoreProgramIdFromStorage(),
      referrer: document.referrer || null,
      page_path: getPageContextDetails().currentPage.page_path ?? null,
      page_url: getSafeLocationHref(),
    }
  } catch {
    return null
  }
}

/**
 * Fire-and-forget diagnostic emitted once on guest cart creation. Takes the Axios response from the
 * create call so the region/locale headers reported are the ones the request actually sent. No-op on
 * the server and whenever telemetry itself throws — it must never affect the cart flow it observes.
 *
 * `cartId` overrides the id read off the response body, for create endpoints whose response carries
 * no cart object (e.g. buy-now returns auth mappings only).
 */
export const reportCartRegionProvenance = (
  res: unknown,
  {
    createSource,
    cartId,
    snapshot,
  }: { createSource?: string; cartId?: string | null; snapshot?: CartRegionSnapshot | null } = {},
): void => {
  try {
    if (!isBrowser()) return

    const response = (res ?? null) as CartCreateResponseLike | null
    const cart = pickCart(response?.data)
    // Prefer the pre-request snapshot so every identifier is as-of cart birth. Falling back to
    // reading now keeps the event useful if a call site does not supply one.
    const client = snapshot ?? captureCartRegionSnapshot()

    const payload = {
      eventId: generateUniqueEventId(),
      cart_id: cartId ?? cart?.id ?? null,
      create_source: createSource ?? null,
      cart_region_id: typeof cart?.regionId === 'number' ? cart.regionId : null,
      cart_currency: cart?.currencyCode ?? null,
      cart_region_resolved: resolveCartRegionId(cart),
      header_region_id_sent: readSentHeader(response, REGION_ID_HEADER),
      header_locale_sent: readSentHeader(response, REGION_LANG_HEADER),
      region_id_cookie: client?.region_id_cookie ?? null,
      locale_cookie: client?.locale_cookie ?? null,
      region_id_local_storage: client?.region_id_local_storage ?? null,
      locale_local_storage: client?.locale_local_storage ?? null,
      server_locale_in_memory: client?.server_locale_in_memory ?? null,
      is_b2b: client?.is_b2b ?? false,
      referrer: client?.referrer ?? null,
      page_path: client?.page_path ?? null,
      page_url: client?.page_url ?? getSafeLocationHref(),
    }

    trackInternalEvent(TELEMETRY_EVENT_NAMES.CART_REGION_PROVENANCE, payload)
  } catch {
    // Diagnostic telemetry must never break the cart flow it observes.
  }
}
